import { FileUploadValidation } from '../../FileUpload/FileUpload.types';
import { ReactNode } from 'react';

// Re-export all types from sub-modules
export * from './components';
export * from './hooks';

/**
 * Single data point in the power profile chart
 */
export interface PowerProfileDataPoint {
  /** Timestamp of the measurement */
  timestamp: string;
  /** Power value in kW */
  power: number;
  /** Day of the week */
  day: string;
  /** Hour of the day (0-23) */
  hour: number;
  /** Index of the day in the week (0-6) */
  dayIndex: number;
}

/**
 * Props for the PowerProfileChart component
 */
export interface PowerProfileChartProps {
  /** Data for the power profile chart */
  data?: PowerProfileDataPoint[];
  /** Height of the chart container */
  height?: number;
  /** Title of the chart (can be string or ReactNode for custom content like icons) */
  title?: ReactNode;
  /** Custom color for the chart area */
  color?: string;
  /** Peak power in kW */
  peakPowerKw?: number;
  /** Average power in kW */
  averagePowerKw?: number;
  /** Working days of the week */
  workingDays?: string[];
  /** Working hours start time (HH:MM format) */
  workingHoursStart?: string;
  /** Working hours end time (HH:MM format) */
  workingHoursEnd?: string;
  /** Orientation warning title text */
  orientationWarningTitle?: string;
  /** Orientation warning message text */
  orientationWarningMessage?: string;
  /** Message to display when no data is available */
  noDataMessage?: string;
  /** Enable paint mode functionality (Click and drag to draw line) */
  enablePaint?: boolean;
  /** Instruction text for paint mode functionality */
  paintInstructionText?: string;
  /** Callback when painting is complete with all updated data */
  onDataPaint?: (data: PowerProfileDataPoint[]) => void;
  /** Custom tick formatter for X-axis */
  xAxisTickFormatter?: (value: string, index: number) => string;
  /** Custom interval for X-axis ticks */
  xAxisInterval?: number | 'preserveStart' | 'preserveEnd' | 'preserveStartEnd';
  titleStyles?: object;
}

/**
 * Power Profile generation modes
 */
export const powerProfileModes = {
  manual: 'manual',
  file: 'file',
} as const;

/**
 * Power Profile generation mode
 * - 'manual': User manually defines power values
 * - 'file': User uploads CSV/XLSX file with power data
 */
export type PowerProfileMode = (typeof powerProfileModes)[keyof typeof powerProfileModes];

/**
 * Power measurement units
 */
export const powerUnits = {
  kW: 'kW',
  kWh: 'kWh',
} as const;

/**
 * Power measurement unit
 * - 'kW': Kilowatts (power)
 * - 'kWh': Kilowatt-hours (energy)
 */
export type PowerUnit = (typeof powerUnits)[keyof typeof powerUnits];

/**
 * Single data point parsed from uploaded file
 */
export interface ParsedDataPoint {
  /** Timestamp of the measurement */
  timestamp: Date;
  /** Power value in the specified unit */
  value: number;
  /** Unit of measurement for the value */
  unit: PowerUnit;
}

/**
 * Complete parsed data from uploaded file
 */
export interface ParsedFileData {
  /** Array of parsed data points */
  data: ParsedDataPoint[];
  /** Original unit from the uploaded file */
  originalUnit: PowerUnit;
  /** Whether parsing encountered errors */
  hasErrors: boolean;
  /** List of parsing errors */
  errors: string[];
  /** List of parsing warnings */
  warnings: string[];
}

/**
 * Power Profile file validation rules - extends FileUpload validation
 * Combines file upload validation with Power Profile specific rules
 */
export interface PowerProfileFileValidation extends FileUploadValidation {
  /** Minimum number of columns required in the file */
  minColumns: number;
  /** Required column names in the file */
  requiredColumns: string[];
  /** Index of the column containing timestamps */
  dateColumnIndex: number;
  /** Index of the column containing power values */
  valueColumnIndex: number;
}

/**
 * Unit conversion configuration
 */
export interface UnitConversion {
  /** Source unit */
  from: PowerUnit;
  /** Target unit */
  to: PowerUnit;
  /** Conversion factor (e.g., 1 kWh = 1 kW × 1h) */
  factor: number;
}
